import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, FileText, CheckCircle2, X } from "lucide-react";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import api from "../../../services/api";

function extractErrorMessage(err, fallback) {
  const errors = err?.response?.data?.errors;
  if (errors && typeof errors === "object") {
    for (const group of Object.values(errors)) {
      if (group && typeof group === "object") {
        const first = Object.values(group)[0];
        if (typeof first === "string") return first;
      }
    }
  }
  return fallback;
}

export function UploadInvoiceModal({ open, onClose }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  function reset() {
    setFile(null);
    setUploading(false);
    setError(null);
    setSuccess(false);
  }

  function handleClose() {
    reset();
    onClose?.();
  }

  function handleFileChange(e) {
    const picked = e.target.files?.[0];
    setError(null);
    setSuccess(false);
    if (picked && picked.type !== "application/pdf") {
      setError(t("invoices.upload_error_type"));
      setFile(null);
      return;
    }
    setFile(picked || null);
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post("/bureau-invoices/upload", formData, {
        headers: { "Content-Type": undefined },
        timeout: 60000,
      });
      setSuccess(true);
      setFile(null);
    } catch (e) {
      setError(extractErrorMessage(e, t("invoices.upload_error_generic")));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("invoices.upload_modal_title")}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {t("common.close")}
          </Button>
          <Button onClick={handleUpload} loading={uploading} disabled={!file}>
            {t("invoices.btn_upload")}
          </Button>
        </>
      }
    >
      <p className="text-[12.5px] text-slate-500">
        {t("invoices.upload_modal_subtitle")}
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handleFileChange}
        className="hidden"
      />

      {!file ? (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full border-2 border-dashed border-slate-200 rounded-xl py-8 flex flex-col items-center gap-2 text-slate-400 hover:border-primary hover:text-primary transition"
        >
          <Upload size={22} strokeWidth={1.5} />
          <span className="text-[12.5px] font-medium">
            {t("invoices.upload_pick_file")}
          </span>
        </button>
      ) : (
        <div className="flex items-center gap-2.5 border border-slate-200 rounded-lg px-3 py-2.5">
          <FileText size={16} strokeWidth={1.75} className="text-primary flex-shrink-0" />
          <span className="flex-1 text-[12.5px] text-slate-700 truncate">{file.name}</span>
          <button
            type="button"
            onClick={() => setFile(null)}
            className="text-slate-400 hover:text-slate-600"
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>
      )}

      {error && <p className="text-[12px] text-rose-600">{error}</p>}
      {success && (
        <p className="text-[12.5px] text-emerald-600 font-medium flex items-center gap-1.5">
          <CheckCircle2 size={14} strokeWidth={1.75} />
          {t("invoices.upload_success")}
        </p>
      )}
    </Modal>
  );
}

export default UploadInvoiceModal;
